import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

import type { BusinessType, UserMarketingType } from '@/types/userMarketingType';
import type { UserType } from '@/types/userType';

import type {
    AgencyIndustryOptionType,
    AiToolsOptionType,
    CompanyIndustryOptionType,
    CompanyPeopleCountOptionType,
    ComparisonOrReviewSiteOptionType,
    ForumsOptionType,
    HearAboutUsOptionType,
    PrimaryGoalOptionType,
    SearchEngineOptionType,
    SocialMediaOptionType,
    SocialMediaToolOptionType,
    SocialProfileManagedOptionType,
    TeamSizeOptionType
} from '@/lib/signup/userInfos/marketingOptions';

dayjs.extend(timezone);

export type UserInfosFormType = {
    first_name: string;
    last_name: string;
    timezone: string;
    primary_goal: PrimaryGoalOptionType['value'];
    hear_about_us: HearAboutUsOptionType['value'];
    hear_about_us_detail?:
        | string
        | SocialMediaOptionType['value']
        | SearchEngineOptionType['value']
        | AiToolsOptionType['value']
        | ComparisonOrReviewSiteOptionType['value']
        | ForumsOptionType['value'];
    social_profiles_managed: SocialProfileManagedOptionType['value'];
    team_size: TeamSizeOptionType['value'];
    social_media_tool: SocialMediaToolOptionType['value'];
    social_media_tool_detail: string;
    company: string;
    company_people_count: CompanyPeopleCountOptionType['value'];
    business_type: BusinessType;
    company_industry?: CompanyIndustryOptionType['value'] | AgencyIndustryOptionType['value'];
    is_franchise?: 'yes' | 'no';
};

type StepThreeFields = Pick<
    UserInfosFormType,
    'company' | 'business_type' | 'company_people_count' | 'company_industry' | 'is_franchise'
>;

export const isStepThreeFormComplete = (fields: StepThreeFields): boolean => {
    if (!fields.company || !fields.business_type) return false;

    switch (fields.business_type) {
        case 'agency':
            return fields.company_people_count != null && fields.company_industry != null;
        case 'enterprise_brand_unique_location':
        case 'enterprise_brand_multi_location':
            return (
                fields.company_people_count != null &&
                fields.is_franchise != null &&
                fields.company_industry != null
            );
        case 'ngo_or_npo_organization':
            return fields.company_people_count != null;
        case 'independent_freelancer':
            return fields.company_industry != null;
        default:
            return false;
    }
};

export const transformFormDataForSubmit = (data: UserInfosFormType) => {
    const businessType =
        data.is_franchise === 'yes' && data.business_type === 'enterprise_brand_unique_location'
            ? 'enterprise_brand_multi_location'
            : data.business_type;

    const companyPeopleCount =
        data.business_type === 'independent_freelancer' ? '1' : data.company_people_count;

    const companyIndustry =
        data.business_type === 'ngo_or_npo_organization' ? null : data.company_industry;

    const dataMarket = {
        primary_goal: data.primary_goal,
        hear_about_us: data.hear_about_us,
        hear_about_us_detail: data.hear_about_us_detail,
        social_profiles_managed: data.social_profiles_managed,
        team_size: data.team_size,
        social_media_tool: data.social_media_tool,
        social_media_tool_detail: data.social_media_tool_detail,
        company: data.company,
        company_people_count: companyPeopleCount,
        business_type: businessType,
        company_industry: companyIndustry
    };

    const dataUser = {
        first_name: data.first_name,
        last_name: data.last_name,
        settings: {
            timezone: data.timezone
        }
    };

    return { dataMarket, dataUser };
};

export const computeFormDefaults = (
    user: UserType,
    userMarket: UserMarketingType
): UserInfosFormType => ({
    first_name: user.first_name,
    last_name: user.last_name,
    timezone: user.settings?.timezone || dayjs.tz.guess(),
    primary_goal: userMarket.primary_goal,
    hear_about_us: userMarket.hear_about_us,
    hear_about_us_detail:
        userMarket?.hear_about_us_detail !== null ? userMarket?.hear_about_us_detail : undefined,
    social_profiles_managed: userMarket.social_profiles_managed,
    team_size: userMarket.team_size,
    social_media_tool: userMarket.social_media_tool,
    social_media_tool_detail: userMarket.social_media_tool_detail,
    company: userMarket.company,
    company_people_count:
        userMarket?.company_people_count !== null ? userMarket?.company_people_count : undefined,
    business_type:
        userMarket.business_type === 'enterprise_brand_multi_location'
            ? 'enterprise_brand_unique_location'
            : userMarket.business_type,
    company_industry: userMarket.company_industry,
    is_franchise:
        userMarket.business_type === 'enterprise_brand_multi_location'
            ? 'yes'
            : userMarket.is_franchise !== null
              ? userMarket?.is_franchise
                  ? 'yes'
                  : 'no'
              : null
});
